import { create } from "zustand";
import { CastError, isCastGone, isNoQueue, isStaleIndex, nextCast, pauseCast, pingCast, resumeCast, staleQueueOf, stopCast } from "../api/cast";
import { CastAdvancer, type AdvanceCommand, type NextOutcome } from "../lib/castSync/castAdvancer";
import { parseQueue } from "../lib/castQueue";
import { kindOf } from "../player";
import { useCastQueueStore } from "./castQueueStore";
import { acceptsTimeline, timelineFromDto, timelineOf, type CastTimeline } from "../lib/castSync/timeline";
import type { CastLocalId } from "../lib/castDock";
import type { StreamSlot } from "../types/stream";
import logger from "../lib/logger";
import { isSameCast, ownCastRecord, storedCastOf, STORED_CAST_TTL_MS } from "../lib/ownCastRecord";

/**
 * Port of Android `OwnVmplCast` (com.project.streaming): the publisher side of your own
 * link/file casts while they are on air. Per cast the slot keep-alive ping, pause/resume
 * (keeping the timeline the server confirms), the stall re-anchor, and one cast stop.
 * Several casts may run at once (one per slot, e.g. a file and a link).
 */

const TAG = "[OwnCast]";

/** Android PING_INTERVAL_SECONDS * 1000. */
export const PING_INTERVAL_MS = 30_000;
/** A stalled publisher re-anchors its cast at most this often. */
export const REANCHOR_EVERY_MS = 5_000;

/**
 * One of your own casts on air. `playing` is what you last asked for (the dock icon) and
 * follows the server's timeline whenever none of your calls is out; `timeline` is the newest
 * one the server confirmed (an answer, the slot list's or an event's), by rev.
 */
export interface OwnCast {
  localId: CastLocalId;
  slot: StreamSlot;
  gcId: number;
  playing: boolean;
  timeline: CastTimeline | null;
  startedAt: number;
}

export interface OwnCastState {
  casts: Partial<Record<CastLocalId, OwnCast>>;
  start(localId: CastLocalId, gcId: number, slot: StreamSlot, timeline: CastTimeline | null): void;
  togglePlay(localId: CastLocalId, positionMs: number): void;
  canToggle(localId: CastLocalId): boolean;
  onTimeline(slotId: number, t: CastTimeline): void;
  reanchor(slotId: number, positionMs: number): void;
  stop(localId: CastLocalId): void;
  stopAll(): void;
  onServerStopped(slotId: number): void;
  castBySlotId(slotId: number): OwnCast | undefined;
  /**
   * Android `reconcile`, after a full slot load (`loaded`: its file/link slots; `gcId`: whose,
   * for the refresh after a stop). A recorded cast of `me` that the server still shows as that
   * very cast with no cast here was left by a closed or reloaded tab: it is stopped (once; a
   * failed stop stays recorded and is retried on the next load). One not kept alive for
   * STORED_CAST_TTL_MS is forgotten without a stop; one the load shows as anything else is
   * forgotten. Recorded casts in slots the load did not include stay.
   */
  reconcile(loaded: StreamSlot[], me: string, gcId?: number): void;
  /** Your link cast's player finished `url` (ENDED): the queue moves on if it is the current item and ends by itself; a photo or radio stream never advances on ENDED (spec 2026-10-16 §4, 2026-10-19 §4.2). */
  onItemEnded(slotId: number, url: string): void;
  /** Your link cast's player cannot load `url`: the item is skipped after LOAD_ERROR_SKIP_MS. */
  onItemFailed(slotId: number, url: string): void;
  /** ⏭ / ⏮ on your link cast (Previous on the first item restarts it). */
  step(localId: CastLocalId, direction: "next" | "previous"): void;
  /** Your link cast has a known queue of 2+ items and no `next` of it is out. */
  canStep(localId: CastLocalId): boolean;
}

/**
 * Slot ids of casts whose publish is in flight (Android `pendingVmplPublish`): not orphans,
 * whatever the slots say meanwhile. The publish flow marks and clears them.
 */
export const pending = new Set<number>();
export function markPending(slotId: number): void {
  pending.add(slotId);
}
export function clearPending(slotId: number): void {
  pending.delete(slotId);
}

/**
 * Other tabs of this browser, asked before an orphan stop. A duplicated tab (and some
 * `window.open`s) starts with a copy of the opener's sessionStorage, so the record alone cannot
 * tell a closed tab's cast from one another tab still runs. Every tab with a cast listens and
 * answers "mine" for the slots it runs. Web only (Android's store is per process).
 */
export const PEER_CHANNEL = "own_vmpl_casts";
/** How long an orphan stop waits for another tab to claim the slot. */
export const PEER_ASK_MS = 300;

type PeerMessage = { type: "ask" | "mine"; slotId: number; id: string };
const isPeerMessage = (m: unknown): m is PeerMessage =>
  typeof m === "object" &&
  m !== null &&
  ((m as PeerMessage).type === "ask" || (m as PeerMessage).type === "mine") &&
  typeof (m as PeerMessage).slotId === "number" &&
  typeof (m as PeerMessage).id === "string";

/** undefined: not opened yet; null: unavailable here (then orphans are stopped without asking). */
let peerChannel: BroadcastChannel | null | undefined;
/** Asks out, by id: `done(true)` when a tab claims the slot; `timer` answers false. */
const peerAsks = new Map<string, { done: (mine: boolean) => void; timer: ReturnType<typeof setTimeout> }>();

function peers(): BroadcastChannel | null {
  if (peerChannel !== undefined) return peerChannel;
  try {
    peerChannel = typeof BroadcastChannel === "function" ? new BroadcastChannel(PEER_CHANNEL) : null;
  } catch (e) {
    logger.warn(`${TAG} no BroadcastChannel; orphan stops will not ask other tabs`, e);
    peerChannel = null;
  }
  if (peerChannel) {
    // Node's (tests) would keep the process alive; browsers have no unref.
    (peerChannel as BroadcastChannel & { unref?: () => void }).unref?.();
    const channel = peerChannel;
    channel.onmessage = (ev: MessageEvent) => {
      const m: unknown = ev.data;
      if (!isPeerMessage(m)) return;
      if (m.type === "mine") {
        peerAsks.get(m.id)?.done(true);
        return;
      }
      if (useOwnCastStore.getState().castBySlotId(m.slotId) == null) return;
      try {
        channel.postMessage({ type: "mine", slotId: m.slotId, id: m.id } satisfies PeerMessage);
      } catch (e) {
        logger.warn(`${TAG} could not answer another tab for slot ${m.slotId}`, e);
      }
    };
  }
  return peerChannel;
}

/** Whether another tab runs a cast in `slotId`: false at once without a channel, else after PEER_ASK_MS unless one answers. */
function askPeers(slotId: number): Promise<boolean> {
  const channel = peers();
  if (!channel) return Promise.resolve(false);
  const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return new Promise((resolve) => {
    const done = (mine: boolean) => {
      const ask = peerAsks.get(id);
      if (!ask) return;
      peerAsks.delete(id);
      clearTimeout(ask.timer);
      resolve(mine);
    };
    peerAsks.set(id, { done, timer: setTimeout(() => done(false), PEER_ASK_MS) });
    try {
      channel.postMessage({ type: "ask", slotId, id } satisfies PeerMessage);
    } catch (e) {
      logger.warn(`${TAG} could not ask other tabs about slot ${slotId}`, e);
      done(false);
    }
  });
}

/** Orphans whose ask to the other tabs is out: a second load does not ask (or stop) again. */
const asking = new Set<number>();

const isPlaying = (t: CastTimeline | null | undefined): boolean | undefined => (t ? t.status === "PLAY" : undefined);

// Per-cast bookkeeping, keyed like Android (SlotLocalId / server slot id).
const pingTimers = new Map<CastLocalId, ReturnType<typeof setTimeout>>();
const resuming = new Set<CastLocalId>();
/** Slots whose cast stop is out right now: nothing sends a second one meanwhile. */
const stopping = new Set<number>();
/** Serialises a cast's pause, resume and re-anchor calls (Android's Mutex). */
const chains = new Map<CastLocalId, Promise<void>>();
const lastReanchorAt = new Map<number, number>();
/** Pause/resume/re-anchor calls of each cast that are out (queued or waiting for the server). */
const callsOut = new Map<CastLocalId, number>();

/** The caster's advance machine per cast (CastAdvancer, cases C55–C64). */
const advancers = new Map<CastLocalId, CastAdvancer>();
/** The pending load-error skip or retry of each cast (one at a time: the advancer has one wake). */
const advanceTimers = new Map<CastLocalId, ReturnType<typeof setTimeout>>();
/** `next` calls of each cast that are out (auto or ⏮ ⏭, queued or waiting): the buttons wait for them. */
const nextsOut = new Map<CastLocalId, number>();
/**
 * F12: bumped by every caster step that took effect (⏮, ⏭, restart) and by every queue identity
 * change. An automatic `next` queued before the bump is for an item no longer on air (a restart
 * keeps the index, so the index alone cannot tell) and is dropped.
 */
const advanceEpochs = new Map<CastLocalId, number>();
const queueOf = (slotId: number) => useCastQueueStore.getState().bySlot[slotId]?.queue ?? null;
const generationOf = (slotId: number) => useCastQueueStore.getState().generation[slotId] ?? 0;

/**
 * A `next` answer that means the cast is over: the ping's cast-gone 400, 404 NO_QUEUE (the slot
 * is free or the cast has no queue) or 403 FORBIDDEN (we are not its caster any more). Only for
 * `next`: a 404 or 403 means something else on other calls.
 */
const isNextCastGone = (e: unknown): boolean =>
  isCastGone(e) || isNoQueue(e) || (e instanceof CastError && e.status === 403);

/** C63 / spec §4 known limit: forget the advance state of the item that was on air, and its pending skip or retry. */
function rearmAdvance(localId: CastLocalId): void {
  advancers.get(localId)?.rearm();
  const timer = advanceTimers.get(localId);
  if (timer != null) clearTimeout(timer);
  advanceTimers.delete(localId);
  advanceEpochs.set(localId, (advanceEpochs.get(localId) ?? 0) + 1);
}

type StoppedListener = (slotId: number, gcId: number) => void;
const stoppedListeners = new Set<StoppedListener>();
type CallFailedListener = (slotId: number) => void;
const callFailedListeners = new Set<CallFailedListener>();

/** Fired with the slot id when a stop the server took or a cast-gone ping needs the slot list refreshed. */
export function onOwnCastStopped(listener: StoppedListener): () => void {
  stoppedListeners.add(listener);
  return () => stoppedListeners.delete(listener);
}

/**
 * Android `callFailed`: a pause/resume call failed, the icon went back to the timeline's state,
 * and the player may follow its (unchanged) timeline again.
 */
export function onOwnCastCallFailed(listener: CallFailedListener): () => void {
  callFailedListeners.add(listener);
  return () => callFailedListeners.delete(listener);
}

function emitStopped(slotId: number, gcId: number) {
  for (const l of stoppedListeners) {
    try {
      l(slotId, gcId);
    } catch (e) {
      logger.error(`${TAG} stopped listener failed`, e);
    }
  }
}

function emitCallFailed(slotId: number) {
  for (const l of callFailedListeners) {
    try {
      l(slotId);
    } catch (e) {
      logger.error(`${TAG} call-failed listener failed`, e);
    }
  }
}

export const useOwnCastStore = create<OwnCastState>()((set, get) => {
  const castOf = (localId: CastLocalId) => get().casts[localId];

  const replace = (cast: OwnCast) => {
    if (!get().casts[cast.localId]) return;
    set((s) => ({ casts: { ...s.casts, [cast.localId]: cast } }));
  };

  const callOut = (localId: CastLocalId) => callsOut.set(localId, (callsOut.get(localId) ?? 0) + 1);

  /** A call is back (only counted for the cast that made it: an ended cast's count went with it). */
  const callBack = (localId: CastLocalId, slotId: number) => {
    if (castOf(localId)?.slot.id !== slotId) return;
    const left = (callsOut.get(localId) ?? 1) - 1;
    if (left > 0) callsOut.set(localId, left);
    else callsOut.delete(localId);
  };

  /** Runs `job` after any earlier call of this cast. */
  const enqueue = (localId: CastLocalId, job: () => Promise<void>) => {
    const next = (chains.get(localId) ?? Promise.resolve()).then(job, job);
    chains.set(localId, next);
  };

  const takeTimeline = (localId: CastLocalId, slotId: number, next: CastTimeline) => {
    const current = castOf(localId);
    if (!current || current.slot.id !== slotId) return;
    if (!acceptsTimeline(current.timeline, next)) return;
    const idle = (callsOut.get(localId) ?? 0) === 0;
    replace({ ...current, timeline: next, playing: idle ? next.status === "PLAY" : current.playing });
  };

  const end = (localId: CastLocalId): OwnCast | undefined => {
    const cast = castOf(localId);
    if (!cast) return undefined;
    const timer = pingTimers.get(localId);
    if (timer != null) clearTimeout(timer);
    pingTimers.delete(localId);
    resuming.delete(localId);
    chains.delete(localId);
    callsOut.delete(localId);
    lastReanchorAt.delete(cast.slot.id);
    const advanceTimer = advanceTimers.get(localId);
    if (advanceTimer != null) clearTimeout(advanceTimer);
    advanceTimers.delete(localId);
    advancers.delete(localId);
    nextsOut.delete(localId);
    advanceEpochs.delete(localId);
    set((s) => {
      const casts = { ...s.casts };
      delete casts[localId];
      return { casts };
    });
    return cast;
  };

  /** The cast stop, once at a time per slot; it leaves the record once the server took it (a failure is retried later). */
  const tellStopped = (slotId: number, gcId: number | undefined) => {
    if (stopping.has(slotId)) return;
    stopping.add(slotId);
    void (async () => {
      try {
        await stopCast(slotId);
        ownCastRecord.remove(slotId);
        // Only a stop the server took refreshes the slots: a failing one must not loop.
        if (gcId != null) emitStopped(slotId, gcId);
      } catch (e) {
        logger.error(`${TAG} cast stop failed for slot ${slotId}`, e);
      } finally {
        stopping.delete(slotId);
      }
    })();
  };

  /** Android's ping loop: wait, ping, repeat; a cast-gone answer ends the cast without a stop call. */
  const schedulePing = (localId: CastLocalId, slot: StreamSlot, gcId: number) => {
    const slotId = slot.id;
    const timer = setTimeout(async () => {
      try {
        await pingCast(slotId);
        if (pingTimers.get(localId) !== timer) return;
        ownCastRecord.put(storedCastOf(slot, Date.now()));
      } catch (e) {
        if (pingTimers.get(localId) !== timer) return;
        if (isCastGone(e)) {
          // Ended elsewhere (missed STOP_STREAM): the slot is not ours any more, so no stop call.
          logger.error(`${TAG} slot ${slotId} is no longer our cast`, e);
          ownCastRecord.remove(slotId);
          if (end(localId)) emitStopped(slotId, gcId);
          return;
        }
        logger.error(`${TAG} ping failed for slot ${slotId}`, e);
      }
      if (pingTimers.get(localId) === timer) schedulePing(localId, slot, gcId);
    }, PING_INTERVAL_MS);
    pingTimers.set(localId, timer);
  };

  const advancerOf = (localId: CastLocalId): CastAdvancer => {
    let a = advancers.get(localId);
    if (!a) {
      a = new CastAdvancer();
      advancers.set(localId, a);
    }
    return a;
  };

  /** Ends a cast the server ended (stopped answer, cast-gone): no stop call; the slot list refreshes. */
  const endedByServer = (localId: CastLocalId, slotId: number) => {
    ownCastRecord.remove(slotId);
    const cast = end(localId);
    if (cast) emitStopped(slotId, cast.gcId);
  };

  const runAdvance = (localId: CastLocalId, slotId: number, cmds: AdvanceCommand[]) => {
    for (const c of cmds) {
      if (c.type === "next") {
        sendNext(localId, slotId, c.fromIndex, undefined, c.seq);
        continue;
      }
      const old = advanceTimers.get(localId);
      if (old != null) clearTimeout(old);
      const generation = generationOf(slotId);
      const timer = setTimeout(() => {
        if (advanceTimers.get(localId) !== timer) return;
        advanceTimers.delete(localId);
        // Another cast in the slot (STOP_STREAM, or a START_STREAM with another queue): not this item's skip.
        if (castOf(localId)?.slot.id !== slotId || generationOf(slotId) !== generation) return;
        const q = queueOf(slotId);
        if (q) runAdvance(localId, slotId, advancerOf(localId).wake(q.index, Date.now()));
      }, Math.max(0, c.mono - Date.now()));
      advanceTimers.set(localId, timer);
    }
  };

  /**
   * One `next` call, serialised with this cast's pause/resume/re-anchor. `seq` is the advancer's
   * token for an automatic call (its answer goes back with it); a caster step has none. Not counted
   * in `callsOut`: the PLAY timeline it answers with should set the play icon.
   */
  const sendNext = (localId: CastLocalId, slotId: number, fromIndex: number, to: number | undefined, seq: number | null) => {
    const auto = seq != null;
    const epoch = advanceEpochs.get(localId) ?? 0;
    const generation = generationOf(slotId);
    nextsOut.set(localId, (nextsOut.get(localId) ?? 0) + 1);
    const before = castOf(localId);
    if (before) replace({ ...before }); // canStep changed
    enqueue(localId, async () => {
      let outcome: NextOutcome = "failed";
      try {
        if (castOf(localId)?.slot.id !== slotId) return;
        // An automatic call queued behind a step (⏮ ⏭, restart included) or a queue identity change,
        // or one whose item the queue already left, is for an item no longer on air: drop it.
        if (
          auto &&
          ((advanceEpochs.get(localId) ?? 0) !== epoch || generationOf(slotId) !== generation || queueOf(slotId)?.index !== fromIndex)
        ) {
          outcome = "moved";
          return;
        }
        const answer = await nextCast(slotId, to == null ? { fromIndex } : { fromIndex, to });
        if (castOf(localId)?.slot.id !== slotId) return;
        if (answer.stopped === true) {
          outcome = "stopped";
          endedByServer(localId, slotId);
          return;
        }
        outcome = "moved";
        const t = timelineFromDto(answer.timeline);
        if (t) takeTimeline(localId, slotId, t);
        const q = parseQueue(answer.queue);
        if (q) useCastQueueStore.getState().take(slotId, q, t?.rev ?? null);
      } catch (e) {
        if (castOf(localId)?.slot.id !== slotId) return;
        if (isStaleIndex(e)) {
          outcome = "stale";
          const q = staleQueueOf(e);
          if (q) useCastQueueStore.getState().adopt(slotId, q);
        } else if (isNextCastGone(e)) {
          logger.error(`${TAG} slot ${slotId} is no longer our cast (next)`, e);
          endedByServer(localId, slotId);
          return;
        } else {
          logger.error(`${TAG} next from ${fromIndex} failed for slot ${slotId}`, e);
        }
      } finally {
        if (castOf(localId)?.slot.id === slotId) {
          const left = (nextsOut.get(localId) ?? 1) - 1;
          if (left > 0) nextsOut.set(localId, left);
          else nextsOut.delete(localId);
          // C63: a caster step that moved (or restarted) the queue, or adopted the server's, re-arms
          // the advancer for the item now playing; a pending skip and queued automatic calls are dropped.
          if (!auto && (outcome === "moved" || outcome === "stale")) rearmAdvance(localId);
          const now = castOf(localId);
          if (now) replace({ ...now });
        }
      }
      if (auto && castOf(localId)?.slot.id === slotId) {
        runAdvance(localId, slotId, advancerOf(localId).result(fromIndex, seq, outcome, Date.now()));
      }
    });
  };

  /**
   * The own link cast in `slotId` and its index, when `url` is the item it plays now. The current
   * item comes from the queue: the cast's own `slot` still holds the first item's media after an advance.
   */
  const currentItem = (slotId: number, url: string) => {
    const cast = get().castBySlotId(slotId);
    const q = queueOf(slotId);
    const item = q?.items[q.index];
    if (!cast || cast.localId !== "VMPL_LINK_1" || !q || item?.url !== url) return null;
    return { localId: cast.localId, index: q.index, item };
  };

  return {
    casts: {},

    canToggle: (localId) => castOf(localId) != null && !resuming.has(localId),

    castBySlotId: (slotId) => Object.values(get().casts).find((c) => c?.slot.id === slotId),

    start(localId, gcId, slot, timeline) {
      get().stop(localId);
      const confirmed = timeline ?? timelineOf(slot);
      const cast: OwnCast = {
        localId,
        slot,
        gcId,
        playing: isPlaying(confirmed) ?? true,
        timeline: confirmed,
        startedAt: Date.now(),
      };
      set((s) => ({ casts: { ...s.casts, [localId]: cast } }));
      ownCastRecord.put(storedCastOf(slot, Date.now()));
      // Listen from now on, to answer another tab that holds a copy of this record.
      peers();
      schedulePing(localId, slot, gcId);
    },

    togglePlay(localId, positionMs) {
      const cast = castOf(localId);
      if (!cast || resuming.has(localId)) return;
      const slotId = cast.slot.id;
      const pause = cast.playing;
      replace({ ...cast, playing: !pause });
      if (!pause) resuming.add(localId);
      callOut(localId);
      enqueue(localId, async () => {
        try {
          const answer = pause ? await pauseCast(slotId, positionMs) : await resumeCast(slotId, undefined);
          const next = timelineFromDto(answer?.timeline);
          if (next) takeTimeline(localId, slotId, next);
        } catch (e) {
          logger.error(`${TAG} ${pause ? "pause" : "resume"} failed for slot ${slotId}`, e);
          // The icon goes back to the newest timeline (answers and events alike).
          const now = castOf(localId);
          if (now && now.slot.id === slotId) {
            replace({ ...now, playing: isPlaying(now.timeline) ?? pause });
            emitCallFailed(slotId);
          }
        } finally {
          if (!pause) {
            resuming.delete(localId);
            // canToggle changed: a fresh record re-renders the dock button even when the answer had no timeline.
            const now = castOf(localId);
            if (now && now.slot.id === slotId) replace({ ...now });
          }
          callBack(localId, slotId);
        }
      });
    },

    reanchor(slotId, positionMs) {
      const cast = get().castBySlotId(slotId);
      if (!cast) return;
      if (!cast.playing || cast.timeline?.status !== "PLAY" || resuming.has(cast.localId)) return;
      const now = Date.now();
      const last = lastReanchorAt.get(slotId);
      if (last != null && now - last < REANCHOR_EVERY_MS) return;
      lastReanchorAt.set(slotId, now);
      const { localId } = cast;
      callOut(localId);
      enqueue(localId, async () => {
        try {
          const next = timelineFromDto((await resumeCast(slotId, positionMs))?.timeline);
          if (next) takeTimeline(localId, slotId, next);
        } catch (e) {
          logger.error(`${TAG} re-anchor failed for slot ${slotId}`, e);
        } finally {
          callBack(localId, slotId);
        }
      });
    },

    onTimeline(slotId, t) {
      const cast = get().castBySlotId(slotId);
      if (cast) takeTimeline(cast.localId, slotId, t);
    },

    stop(localId) {
      const cast = end(localId);
      if (cast) tellStopped(cast.slot.id, cast.gcId);
    },

    stopAll() {
      for (const localId of Object.keys(get().casts) as CastLocalId[]) get().stop(localId);
    },

    onServerStopped(slotId) {
      ownCastRecord.remove(slotId);
      const cast = get().castBySlotId(slotId);
      if (cast) end(cast.localId);
    },

    reconcile(loaded, me, gcId) {
      const now = Date.now();
      for (const entry of ownCastRecord.entries()) {
        // A wall clock set backwards makes the age negative: treat that as expired too.
        const age = now - entry.aliveAtMs;
        if (!(age >= 0 && age <= STORED_CAST_TTL_MS)) {
          ownCastRecord.remove(entry.slotId);
          continue;
        }
        const slot = loaded.find((x) => x.id === entry.slotId);
        if (!slot) continue;
        const slotId = entry.slotId;
        const busyHere = () => get().castBySlotId(slotId) != null || pending.has(slotId) || stopping.has(slotId);
        if (busyHere() || asking.has(slotId)) continue;
        if (!isSameCast(entry, slot, me)) {
          ownCastRecord.remove(slotId);
          continue;
        }
        // Web: a duplicated tab holds a copy of the record; the tab that runs the cast claims it.
        if (!peers()) {
          tellStopped(slotId, gcId);
          continue;
        }
        asking.add(slotId);
        void askPeers(slotId).then((mine) => {
          asking.delete(slotId);
          if (mine) ownCastRecord.remove(slotId);
          else if (!busyHere()) tellStopped(slotId, gcId);
        });
      }
    },

    onItemEnded(slotId, url) {
      const at = currentItem(slotId, url);
      // C66, C67 (spec 2026-10-19 §4.2): a photo or a radio stream stays until ⏭, whatever its player says.
      if (at && kindOf(at.item).caps.endsByItself) runAdvance(at.localId, slotId, advancerOf(at.localId).ended(at.index, Date.now()));
    },

    onItemFailed(slotId, url) {
      const at = currentItem(slotId, url);
      if (at) runAdvance(at.localId, slotId, advancerOf(at.localId).loadFailed(at.index, Date.now()));
    },

    canStep(localId) {
      const cast = castOf(localId);
      if (!cast || localId !== "VMPL_LINK_1" || (nextsOut.get(localId) ?? 0) > 0) return false;
      return (queueOf(cast.slot.id)?.count ?? 0) > 1;
    },

    step(localId, direction) {
      const cast = castOf(localId);
      if (!cast || !get().canStep(localId)) return;
      const q = queueOf(cast.slot.id);
      if (!q) return;
      sendNext(localId, cast.slot.id, q.index, direction === "next" ? undefined : Math.max(0, q.index - 1), null);
    },
  };
});

/**
 * Spec §4 known limit: `next` carries no cast identity. When a slot's queue generation moves
 * (STOP_STREAM, or a START_STREAM whose queue differs), the own cast there re-arms: its pending
 * skip, retry and queued automatic `next` belong to another cast.
 */
useCastQueueStore.subscribe((q, prev) => {
  if (q.generation === prev.generation) return;
  for (const cast of Object.values(useOwnCastStore.getState().casts)) {
    if (cast && (q.generation[cast.slot.id] ?? 0) !== (prev.generation[cast.slot.id] ?? 0)) rearmAdvance(cast.localId);
  }
});

/** Tests: clears timers and state, no API calls. The tab's record (sessionStorage) is left alone. */
export function resetOwnCastStore(): void {
  pending.clear();
  asking.clear();
  // Asks out are dropped unanswered: no stop follows a reset.
  for (const ask of peerAsks.values()) clearTimeout(ask.timer);
  peerAsks.clear();
  try {
    peerChannel?.close();
  } catch {
    // Already closed.
  }
  peerChannel = undefined;
  for (const t of pingTimers.values()) clearTimeout(t);
  pingTimers.clear();
  resuming.clear();
  stopping.clear();
  chains.clear();
  lastReanchorAt.clear();
  callsOut.clear();
  for (const t of advanceTimers.values()) clearTimeout(t);
  advanceTimers.clear();
  advancers.clear();
  nextsOut.clear();
  advanceEpochs.clear();
  useOwnCastStore.setState({ casts: {} });
}
